import { useMemo, useEffect } from "react";
// import type { StrategyNode } from "../../types/BaseNode";
import { SingleNode } from "../../../../types/SingleNode";
import type { StrategyFamily } from "../../../../types/Strategy";
import { useDataTypes, type DataTypeSpec } from "../../../../hooks/useDataTypes";
import { DynamicField } from '../../common/DynamicField';
import { groupByCategory } from "../../../../utils/groupBy";
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';

import "./ComponentCard.css";

interface Props {
  node: SingleNode;
  parentOperator?: string;
  slot?: string;
  parentType?: string;
  strategyFamily?: StrategyFamily;
}

function normalizeSingleCardConfig(config: any): { typeId: string; props: Record<string, any> } {
  if (!config || typeof config !== 'object') {
    return { typeId: '', props: {} };
  }

  let cursor = { ...config };
  let typeId = cursor.typeId ?? cursor.type_id ?? '';
  while (cursor.props && typeof cursor.props === 'object' && !Array.isArray(cursor.props)) {
    const nested = cursor.props;
    if (!typeId && (nested.typeId ?? nested.type_id)) {
      typeId = nested.typeId ?? nested.type_id;
    }
    cursor = nested;
  }

  const props = Object.fromEntries(
    Object.entries(cursor).filter(([key]) => !['typeId', 'type_id', 'start_date', 'end_date'].includes(key))
  );

  return { typeId, props };
}

export function SingleComponentCard({ node, parentOperator, slot, parentType, strategyFamily = 'base' }: Props) {
  const { onUpdate, isInteractionLocked } = useNodeOperations();
  const { loading, error, dataTypes, getOptions, getDataTypeById } = useDataTypes();

  const { typeId, props } = normalizeSingleCardConfig((node as any).config);
  const selectedType = useMemo(() => getDataTypeById(typeId), [getDataTypeById, typeId]);
  const isRestrictedMeepSourceSlot =
    (parentType === 'MeepMeep' && slot === 'main_market') ||
    (parentType === 'Regression' && typeof slot === 'string' && slot.startsWith('input-'));

  const handleTypeChange = (newTypeId: string) => {
    if (isInteractionLocked) return;

    const updated = new SingleNode(node.id, node.title, node.isManualRename);
    const newTypeSpec = getDataTypeById(newTypeId);
    const newProps: Record<string, any> = {};
    if (newTypeSpec) {
      for (const p of newTypeSpec.properties) {
        newProps[p.key] = p.default ?? null;
      }
    }

    updated.config = { typeId: newTypeId, props: newProps };
    onUpdate(node.id, updated);
  };

  const handlePropChange = (key: string, value: any) => {
    if (isInteractionLocked) return;
    const updated = new SingleNode(node.id, node.title, node.isManualRename);
    updated.config = { typeId, props: { ...props, [key]: value } };
    onUpdate(node.id, updated);
  };

  // 🎯 Calculate filtered data types based on nature
  const filteredDataTypes = useMemo(() => {
    if (!dataTypes.length) return [];
    const familyFiltered = dataTypes.filter(dt => (dt.strategy_grammars ?? ['base']).includes(strategyFamily));

    // MeepMeep execution market and Regression feature leaves must stay on AlphaBook.
    if (isRestrictedMeepSourceSlot) {
      return familyFiltered.filter(dt => dt.translation?.can_be_meepmeep_source);
    }

    // PnL Primary = translations with pnl_primary flag
    if (parentOperator === 'PnL' && (slot === 'entry' || slot === 'exit')) {
      return familyFiltered.filter(dt => dt.translation?.can_be_pnl_primary);
    }

    return familyFiltered;
  }, [dataTypes, isRestrictedMeepSourceSlot, parentOperator, slot, strategyFamily]);

  // 🤖 Auto-select/correct if the slot is restricted or if exactly one valid type exists.
  useEffect(() => {
    if (isRestrictedMeepSourceSlot && filteredDataTypes.length === 1 && typeId !== filteredDataTypes[0].id) {
      handleTypeChange(filteredDataTypes[0].id);
      return;
    }
    if (!typeId && filteredDataTypes.length === 1) {
      handleTypeChange(filteredDataTypes[0].id);
    }
  }, [filteredDataTypes, isRestrictedMeepSourceSlot, typeId]);

  // If loading and we have no data types yet, show loading
  // BUT if we are re-loading and already have dataTypes, don't hide the UI
  if (loading && dataTypes.length === 0) return <div className="p-md text-muted italic">Loading available data types...</div>;
  if (error && dataTypes.length === 0) return <div className="nature-warning-box">Error fetching data types: {error}</div>;

  const stopPropagation = (e: React.UIEvent | React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="internal-card-container" onPointerDown={stopPropagation}>
      {/* Data type selector */}
      <div className="field-row">
        <label className="field-label">Data Type</label>

        <div className="flex-1 flex items-center gap-xs">
          <div className="custom-select-container flex-1">
            <select
              value={typeId}
              onPointerDown={stopPropagation}
              onMouseDown={stopPropagation}
              onClick={stopPropagation}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="native-select"
              disabled={isInteractionLocked}
            >
              <option value="">-- Select Type --</option>
              {Object.entries(groupByCategory<DataTypeSpec>(filteredDataTypes)).map(([category, items]) => (
                <optgroup key={category} label={category}>
                  {items.map((dt) => (
                    <option key={dt.id} value={dt.id}>
                      {dt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <div className="select-label-overlay">
              <span>{dataTypes.find(dt => dt.id === typeId)?.label || "-- Select Type --"}</span>
              <span className="select-arrow">▼</span>
            </div>
          </div>

          {selectedType?.description && (
            <span
              title={selectedType.description}
              style={{ cursor: "help", opacity: 0.5, fontSize: "1rem" }}
              onPointerDown={stopPropagation}
            >
              🛈
            </span>
          )}
        </div>
      </div>

      {/* Dynamic fields */}
      {!!typeId && selectedType && (
        <div className="flex flex-col gap-sm" onPointerDown={stopPropagation}>
          {selectedType.properties.map((p) => (
            <DynamicField
              key={p.key}
              spec={p}
              context={props}
              value={props[p.key]}
              onChange={(v) => handlePropChange(p.key, v)}
              getOptions={getOptions}
            />
          ))}
        </div>
      )}
    </div>
  );
}
